/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import styled from 'styled-components';
import {dlsColor, dlsLayout, dlsTypography} from '@ion/dls/src/theme/theme';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';

export interface CardItemRowProps {
  label: JSX.Element | string;
  value: JSX.Element | string;
  isLabelColon?: boolean;
  isDisabled?: boolean;
  isBottomMargin?: boolean;
  isCenterAligned?: boolean;
  handleClick?: () => void;
  stylesLeft?: {
    [key: string]: string;
  }
  stylesRight?: {
    [key: string]: string;
  },
  rowClass?: string;
  isTextRightAligned?: boolean;
  breakWord?: {
    [key: string]: string;
  }
  isCondensedVariant?: boolean;
  isCondensedVariantLarge?: boolean;
  highlight?: boolean;
}

export const StyledBodyTypography = styled(BodyTypography)`
    color: ${(props) => props.color || (props.disabled ? dlsColor('grey4') : dlsColor('grey3'))};
    max-width: 245px;
`;

const useStyles = makeStyles(theme => ({
  row : {
    display        : 'flex',
    justifyContent : 'space-between',
    alignItems     : ({isCenterAligned}: {isCenterAligned?: boolean
    }) => isCenterAligned ? 'center' : 'baseline',
    marginBottom : ({isBottomMargin}: {isBottomMargin?: boolean,
      handleClick?: unknown}) => isBottomMargin ? 6 : 0
  },
  rowItem : {
    display : 'flex'
  },
  rowLabel : {
    ...dlsTypography('chipButton')
  },
  condensedRowLabel : {
    fontSize      : '12px',
    fontFamily    : 'Roboto',
    lineHeight    : '16px',
    fontWeight    : 500,
    letterSpacing : '1px',
    textTransform : 'uppercase',
    color         : `${dlsColor('grey2')} !important`,
    padding       : '0 !important',
    marginRight   : '10px',
    marginBottom  : '0 !important'
  },
  condensedRowValue : {
    fontSize : ({isCondensedVariantLarge}:
    {isCondensedVariantLarge?: boolean}) => isCondensedVariantLarge ? '16px' : '14px',
    fontFamily : 'Roboto',
    lineHeight : '24px',
    fontWeight : ({highlight}: {highlight?: boolean}) => (highlight ? 500 : 400),
    padding    : '0 !important',
    color      : ({highlight}: {highlight?: boolean}) =>
      `${highlight ? dlsColor('caBlue') : dlsColor('grey2')} !important`
  },
  status : {
    marginRight : dlsLayout('spacing') * 2
  }
}));

/**
 * Defines the TextField component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 * import React from 'react';
 * import CardItemRow from '@ion/dls/src/layout/CardItemRow';
 *
 * function MyComponent {
 *   return (
 *     <React.Fragment>
 *       <CardItemRow label='Destination'
 *         value='Fairfield Steel, Fresno, CA' />
 *       <CardItemRow label='Location'
 *         value='In Vehicle'
 *         isLabelColon={false} />
 *       <CardItemRow label='Location'
 *         value='In Vehicle'
 *         isDisabled />
 *       <CardItemRow isDisabled
 *         label='Location'
 *         value={<div>0 Items</div>} />
 *     </React.Fragment>
 *   );
 * }
 *
 * ```
 *
 */
const CardItemRow = ({
  label,
  value,
  isLabelColon = true,
  isDisabled = false,
  isBottomMargin = true,
  isCenterAligned = true,
  stylesLeft = {},
  stylesRight = {},
  handleClick,
  rowClass,
  isTextRightAligned = false,
  breakWord = {},
  isCondensedVariant = false,
  isCondensedVariantLarge = false,
  highlight = false
}: CardItemRowProps) => {
  const classes = useStyles({
    isCondensedVariantLarge,
    isBottomMargin,
    isCenterAligned,
    handleClick,
    highlight
  });
  return (
    <div className={classnames(classes.row, rowClass)} onClick={handleClick}>
      <div className={stylesLeft ? null : classes.rowItem} style={{...stylesLeft}}>
        {typeof label === 'string' ? (
          <>
            <StyledBodyTypography className={isCondensedVariant ? classes.condensedRowLabel : classes.rowLabel}
              disabled={isDisabled}>
              {`${label}${isLabelColon ? ':' : ''}`}
            </StyledBodyTypography>
          </>
        )
          : (
            <>{label}</>
          )}
      </div>
      <div className={stylesRight ? null : classes.rowItem} style={{...stylesRight}}>
        {typeof value === 'string' ? (
          <>
            <StyledBodyTypography className={isCondensedVariant ? classes.condensedRowValue : null}
              disabled={isDisabled}
              align={isTextRightAligned ? 'right' : 'left'}
              style={{...breakWord}}>
              {value}
            </StyledBodyTypography>
          </>
        )
          : (
            <>{value}</>
          )}
      </div>
    </div>
  );
};

export default CardItemRow;
